import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import Toast, { ToastType } from '../components/Toast';

interface NotificationContextType {
  unreadCount: number;
  refreshNotifications: () => void;
}

interface ToastContextType {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

const NotificationContext = createContext<NotificationContextType>({
  unreadCount: 0,
  refreshNotifications: () => {},
});

const ToastContext = createContext<ToastContextType>({
  showSuccess: () => {},
  showError: () => {},
  showWarning: () => {},
  showInfo: () => {},
});

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<{ visible: boolean; message: string; type: ToastType }>({
    visible: false,
    message: '',
    type: 'info',
  });

  const showToast = useCallback((message: string, type: ToastType) => {
    setToast({ visible: true, message, type });
  }, []);

  const toastValue: ToastContextType = {
    showSuccess: useCallback((message: string) => showToast(message, 'success'), [showToast]),
    showError: useCallback((message: string) => showToast(message, 'error'), [showToast]),
    showWarning: useCallback((message: string) => showToast(message, 'warning'), [showToast]),
    showInfo: useCallback((message: string) => showToast(message, 'info'), [showToast]),
  };

  return (
    <NotificationContext.Provider value={{ unreadCount: 0, refreshNotifications: () => {} }}>
      <ToastContext.Provider value={toastValue}>
        {children}
        <Toast
          visible={toast.visible}
          message={toast.message}
          type={toast.type}
          onHide={() => setToast(prev => ({ ...prev, visible: false }))}
        />
      </ToastContext.Provider>
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}

export function useNotification() {
  return useContext(ToastContext);
}

export default NotificationContext;
